import { Injectable } from '@angular/core';

export enum ValidationIssueReason {
  UnresolvedRaw = 'unresolved-raw',
  ChipMissingField = 'chip-missing-field',
  StructuralAttrUnresolved = 'structural-attr-unresolved',
  HeaderLoopWithoutBodyLoop = 'header-loop-without-body-loop'
}

export interface ValidationIssue {
  path: string;
  reason: ValidationIssueReason;
  context?: string;
}

const TOKEN_PATTERN = /\[\[([^\]]+)\]\]/g;
const CHIP_PATTERN = /<span[^>]+class="cke-placeholder-chip"[^>]+data-path="([^"]+)"[^>]*>/g;
const DATA_TPL_EACH_PATTERN = /data-tpl-each="([^"]+)"/g;
const LOOP_ELEMENT_OPEN_PATTERN = /<([a-zA-Z][a-zA-Z0-9-]*)[^>]*\sdata-tpl-each="[^"]*"[^>]*>/g;
const DATA_TPL_IF_PATTERN = /data-tpl-if="([^"]+)"/g;
const DATA_TPL_ATTR_PATTERN = /data-tpl-attr-[a-z-]+="\s*([^"]+)\s*"/g;
const TH_DATA_TPL_EACH_PATTERN = /<th[^>]+data-tpl-each="([^"]+)"[^>]*>/g;
const TR_DATA_TPL_EACH_PATTERN = /<tr[^>]+data-tpl-each="([^"]+)"[^>]*>/;
const INDEX_KEYWORD = '@index';
const THIS_KEYWORD = 'this';
const PARENT_PREFIX = '../';
const COMPUTED_DATA_PREFIX = 'computedData.';
const OPERAND_PATH_PATTERN = /(?:\.\.\/)*[@A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z0-9_]+)*/g;
const STRING_LITERAL_PATTERN = /'[^']*'/g;
const RESERVED_OPERAND_LITERALS: ReadonlySet<string> = new Set(['true', 'false']);
const PIPE_CHARACTER = '|';

@Injectable({ providedIn: 'root' })
export class ReportTemplateValidationService {
  validate(layoutHtml: string, knownPaths: string[]): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const knownSet = new Set(knownPaths);

    const loopRanges = this.findLoopElementRanges(layoutHtml);
    let blockDepth = 0;
    let match: RegExpExecArray | null;
    TOKEN_PATTERN.lastIndex = 0;

    while ((match = TOKEN_PATTERN.exec(layoutHtml)) !== null) {
      const token = match[1].trim();

      if (token.startsWith('#each') || token.startsWith('#if')) {
        blockDepth++;
        continue;
      }
      if (token === '/each' || token === '/if') {
        if (blockDepth > 0) blockDepth--;
        continue;
      }

      for (const operand of this.extractOperandPaths(token)) {
        const tokenIndex = match.index;
        const insideLoop = blockDepth > 0 || loopRanges.some(range => tokenIndex >= range.start && tokenIndex < range.end);
        if (insideLoop && this.isLoopLocalReference(operand)) continue;

        // computedData.* is the engine-computed namespace the mapper populates for widget loop
        // templates (e.g. computedData.gravimetricDuration). It is never a user field reference,
        // so it is always resolvable and must not be flagged as unresolved.
        if (operand.startsWith(COMPUTED_DATA_PREFIX)) continue;

        if (!knownSet.has(operand)) {
          issues.push({ path: operand, reason: ValidationIssueReason.UnresolvedRaw });
        }
      }
    }

    CHIP_PATTERN.lastIndex = 0;
    while ((match = CHIP_PATTERN.exec(layoutHtml)) !== null) {
      const fieldPath = match[1].trim();
      if (!knownSet.has(fieldPath)) {
        issues.push({ path: fieldPath, reason: ValidationIssueReason.ChipMissingField });
      }
    }

    this.validateStructuralAttributes(layoutHtml, knownSet, issues);

    return this.deduplicateIssues(issues);
  }

  private findLoopElementRanges(html: string): { start: number; end: number }[] {
    const ranges: { start: number; end: number }[] = [];
    const opener = new RegExp(LOOP_ELEMENT_OPEN_PATTERN.source, 'g');
    let open: RegExpExecArray | null;
    while ((open = opener.exec(html)) !== null) {
      const start = open.index + open[0].length;
      const tagPattern = new RegExp(`<(/?)${open[1]}(?![\\w-])[^>]*>`, 'gi');
      tagPattern.lastIndex = start;
      let depth = 1;
      let end = html.length;
      let tag: RegExpExecArray | null;
      while ((tag = tagPattern.exec(html)) !== null) {
        if (tag[1]) depth--;
        else if (!tag[0].endsWith('/>')) depth++;
        if (depth === 0) {
          end = tag.index;
          break;
        }
      }
      ranges.push({ start, end });
    }
    return ranges;
  }

  private validateStructuralAttributes(html: string, knownSet: Set<string>, issues: ValidationIssue[]): void {
    let match: RegExpExecArray | null;

    DATA_TPL_EACH_PATTERN.lastIndex = 0;
    while ((match = DATA_TPL_EACH_PATTERN.exec(html)) !== null) {
      const expr = match[1].trim();
      if (!this.isStructuralExpressionKnown(expr, knownSet)) {
        issues.push({ path: expr, reason: ValidationIssueReason.StructuralAttrUnresolved, context: 'data-tpl-each' });
      }
    }

    DATA_TPL_IF_PATTERN.lastIndex = 0;
    while ((match = DATA_TPL_IF_PATTERN.exec(html)) !== null) {
      const expr = match[1].trim().replace(/^!/, '');
      if (!this.isStructuralExpressionKnown(expr, knownSet)) {
        issues.push({ path: expr, reason: ValidationIssueReason.StructuralAttrUnresolved, context: 'data-tpl-if' });
      }
    }

    DATA_TPL_ATTR_PATTERN.lastIndex = 0;
    while ((match = DATA_TPL_ATTR_PATTERN.exec(html)) !== null) {
      const expr = match[1].trim();
      if (!this.isStructuralExpressionKnown(expr, knownSet)) {
        issues.push({ path: expr, reason: ValidationIssueReason.StructuralAttrUnresolved, context: 'data-tpl-attr' });
      }
    }

    TH_DATA_TPL_EACH_PATTERN.lastIndex = 0;
    const hasHeaderLoop = TH_DATA_TPL_EACH_PATTERN.test(html);
    if (hasHeaderLoop && !TR_DATA_TPL_EACH_PATTERN.test(html)) {
      issues.push({ path: '', reason: ValidationIssueReason.HeaderLoopWithoutBodyLoop, context: 'table' });
    }
  }

  private isStructuralExpressionKnown(expr: string, knownSet: Set<string>): boolean {
    const operands = this.extractOperandPaths(expr);
    if (!operands.length) return true;

    return operands.every(operand =>
      this.isLoopLocalReference(operand) || operand.startsWith(COMPUTED_DATA_PREFIX) || knownSet.has(operand)
    );
  }

  private extractOperandPaths(expression: string): string[] {
    const beforePipes = this.takeExpressionBeforePipe(expression).replace(STRING_LITERAL_PATTERN, ' ');
    const operands: string[] = beforePipes.match(OPERAND_PATH_PATTERN) ?? [];
    return operands.filter(operand => !RESERVED_OPERAND_LITERALS.has(operand.toLowerCase()));
  }

  private takeExpressionBeforePipe(expression: string): string {
    for (let index = 0; index < expression.length; index++) {
      if (expression[index] !== PIPE_CHARACTER) continue;
      if (expression[index + 1] === PIPE_CHARACTER) {
        index++;
        continue;
      }
      return expression.slice(0, index);
    }
    return expression;
  }

  private isLoopLocalReference(token: string): boolean {
    let path = token;
    while (path.startsWith(PARENT_PREFIX)) {
      path = path.slice(PARENT_PREFIX.length).trim();
    }
    if (path === INDEX_KEYWORD || path === THIS_KEYWORD) return true;
    if (path.startsWith(`${THIS_KEYWORD}.`)) return true;
    return !path.includes('.');
  }

  private deduplicateIssues(issues: ValidationIssue[]): ValidationIssue[] {
    const seen = new Set<string>();
    return issues.filter(issue => {
      const key = `${issue.path}::${issue.reason}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
}
